'use client';

import { Typography } from '@musinsa/mds';
import { convertToFullUrl, platformIdToSnsType } from '@musinsa/services-curator';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSnsDuplicateCheck } from '../../common/hooks/useSnsDuplicateCheck';
import { URLInputWithPrefix } from '../../join/funnels/SNSFunnel/components/URLInputWithPrefix';
import type { Platform } from '../../join/funnels/SNSFunnel/constants/platforms';
import type { SNSLinkEditFormData } from './SNSLinkEditForm';

interface SnsUrlInputProps {
  platform?: Platform;
  value: string | undefined;
  onChange: (value: string) => void;
  index: number; // SNS 링크 인덱스
  className?: string;
  debounceDelay?: number;
}

const DUPLICATE_ERROR_MESSAGE = '이미 가입된 계정 입니다. SNS 정보를 확인해 주세요.';

const shouldPerformDuplicateCheck = (
  currentValue: string,
  initialValue: string,
  platform: Platform | undefined,
): boolean => {
  if (!platform) return false;

  const trimmedValue = currentValue.trim();
  return !!trimmedValue && trimmedValue !== initialValue.trim();
};

export function SnsUrlInput({
  platform,
  value,
  onChange,
  index,
  className,
  debounceDelay = 500,
}: SnsUrlInputProps) {
  const [localValue, setLocalValue] = useState(value || '');
  const initialValueRef = useRef(value || '');
  const { setError, clearErrors } = useFormContext<SNSLinkEditFormData>();

  const fieldName = `snsLinks.${index}.url` as const;

  const normalizedUrl = useMemo(() => {
    if (!platform) return '';
    const snsType = platformIdToSnsType(platform.id);
    return convertToFullUrl(snsType, localValue);
  }, [platform, localValue]);

  const shouldCheckDuplicate = useMemo(
    () => shouldPerformDuplicateCheck(localValue, initialValueRef.current, platform),
    [localValue, platform],
  );

  const {
    isDuplicated,
    isChecking,
    error: duplicateError,
  } = useSnsDuplicateCheck({
    url: normalizedUrl,
    enabled: shouldCheckDuplicate,
    debounceDelay,
  });

  // 로컬 값 변경 핸들러
  const handleLocalValueChange = (newValue: string) => {
    setLocalValue(newValue);
    onChange(newValue);
  };

  // 초기화 핸들러
  const handleClear = () => {
    setLocalValue('');
    onChange('');
    initialValueRef.current = '';
  };

  // 외부 value 변경 시 동기화
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value || '');
      initialValueRef.current = value || '';
    }
  }, [value, localValue]);

  // 중복 검사 결과에 따른 폼 에러 설정
  useEffect(() => {
    if (isChecking) {
      setError(fieldName, {
        type: 'checking',
      });
      return;
    }

    // 중복일 때 에러 설정
    if (isDuplicated) {
      setError(fieldName, {
        type: 'duplicate',
        message: DUPLICATE_ERROR_MESSAGE,
      });
      return;
    }

    // 중복이 아닐 때 에러 클리어 (null이 아닌 false일 때만)
    if (isDuplicated === false) {
      clearErrors(fieldName);
    }

    // isDuplicated가 null인 경우(검사 안함)는 아무것도 하지 않음
  }, [isChecking, isDuplicated, fieldName, setError, clearErrors]);

  return (
    <div className="flex flex-col">
      <URLInputWithPrefix
        platform={platform}
        value={localValue}
        onChange={handleLocalValueChange}
        onClear={handleClear}
        className={className}
        showClearButton
      />

      {/* 중복검사 에러 메시지 */}
      {!isChecking && isDuplicated && (
        <Typography variant="textEtc11pxReg" colorScheme="red" className="mt-1">
          {DUPLICATE_ERROR_MESSAGE}
        </Typography>
      )}

      {/* 네트워크 에러 메시지 */}
      {!isChecking && duplicateError && !isDuplicated && (
        <Typography variant="textEtc11pxReg" colorScheme="red" className="mt-1">
          중복 검사 중 오류가 발생했습니다. 다시 시도해주세요.
        </Typography>
      )}
    </div>
  );
}
